import React, {useEffect, useState} from "react";
import * as ApiCalls from "./components/Serving/ApiCalls";

import Loading from "./components/Serving/Loading";

import TopBar from "./components/TopBar";
import MainPart from "./components/MainPart";
import MessageBox from "./components/Serving/MessageBox";
import {BrowserRouter} from "react-router-dom";

import "./App.css";
import {UserInfoType} from "./Types/UserInfo";

export default function App(acquiredToken: any) {
    const azureToken = acquiredToken.acquiredToken;
    const [userId, setUserId] = useState<string>("");
    const [userInfo, setUserInfo] = useState<UserInfoType>();
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [messageBoxProp, setMessageBoxProp] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        startUp();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function startUp() {
        setIsLoading(true);

        if (process.env.REACT_APP_ENVIRONMENT === "local") {
            console.log("Azure token in Local env", azureToken);
        }

        if (azureToken?.idTokenClaims?.roles) {
            setUserRoles(azureToken.idTokenClaims.roles);
        }

        await ApiCalls.getUserInfo(azureToken.account.localAccountId, azureToken)
            .then((res) => {
                setUserId(res.Id);
                setUserInfo(res);
                setIsLoading(false);
                //preload profile image
                if (res.imageUrl) {
                    const img = new Image();
                    img.src = res.imageUrl;
                }

                ApiCalls.log("INFO", "AuthorsPortal successful login", "AuthorsPortal", "", "", res.Id, azureToken);
                ApiCalls.logMetric("AuthorsPortal", "Successful login to AuthorsPortal", "Success", "", "Successful login to AuthorsPortal", "", "", res.Id, azureToken);
            })
            .catch((err) => {
                setMessageBoxProp("errorGettingUserInfo");
                console.log("get userinfo error", err);
                ApiCalls.log(
                    "ERROR",
                    "Failed: AuthorsPortal: get userinfo call failed.",
                    "AuthorsPortal",
                    "",
                    "",
                    azureToken?.account?.localAccountId,
                    azureToken
                );
                ApiCalls.logMetric(
                    "AuthorsPortal",
                    "Failed: AuthorsPortal: get userinfo call failed.",
                    "Failed",
                    "",
                    "Failed: AuthorsPortal: get userinfo call failed.",
                    "",
                    "",
                    azureToken?.account?.localAccountId,
                    azureToken
                );
            });

        setWebsiteIconAndTitle();
    }


    function setWebsiteIconAndTitle() {
        if (process.env.REACT_APP_ENVIRONMENT !== "production") {
            const logoLink = document.querySelector("link[rel~='icon']");
            const appleLogoLink = document.querySelector("link[rel~='apple-touch-icon']");

            document.title = process.env.REACT_APP_TITLE ?? "Forfatterportal";

            if (logoLink instanceof HTMLLinkElement) {
                logoLink.href = process.env.REACT_APP_LOGO ?? "";
            }
            if (appleLogoLink instanceof HTMLLinkElement) {
                appleLogoLink.href = process.env.REACT_APP_LOGO ?? "";
            }
        }
    }

    // Show latest profile changes even if it is unvalidated yet
    useEffect(() => {
        async function fetchUnresolvedValidations() {
            if (userInfo && userInfo.Id) {
                await checkUnresolvedValidation();
            }
        }

        fetchUnresolvedValidations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.Id]);

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userInfo?.Id, azureToken);
    };

    const checkUnresolvedValidation = async () => {
        await ApiCalls.getUnresolvedValidations(userInfo, azureToken)
            .then((res) => {
                if (res.length === 0) return;

                const sortedRes = [...res].sort((a, b) =>
                    new Date(b.CreatedUtc).getTime() - new Date(a.CreatedUtc).getTime()
                );

                if (sortedRes[0].UserId === userInfo?.Id) {
                    let latestChanges = sortedRes[0];
                    setUserInfo((prevUserInfo) => ({
                        ...prevUserInfo,
                        ...(latestChanges.DescriptionChanged && {Description: latestChanges.Description}),
                        ...(latestChanges.EmailChanged && {Email: latestChanges.Email}),
                        ...(latestChanges.EmployerChanged && {Employer: latestChanges.Employer}),
                        ...(latestChanges.ImageUrlChanged && {ImageUrl: latestChanges.ImageUrl}),
                        ...(latestChanges.TitleChanged && {Title: latestChanges.Title}),
                    }));
                }
            })
            .catch((e) => {
                console.log("error on GetUnresolvedValidation", e);
                log("ERROR", "Failed GetUnresolvedValidation " + e.message, "Author portal", "", "");
            });
    };

    // Alert when a new version comes ----------
    useEffect(() => {
        let initialIndexFile: string | null = null;

        const checkForUpdate = () => {
            ApiCalls.getIndexFile()
                .then((res) => {
                    if (initialIndexFile === null) {
                        initialIndexFile = res;
                    } else if (res && res !== initialIndexFile) {
                        setMessageBoxPropFunc("newVersion");
                    }
                })
                .catch((e) => console.log("Failed to get index file", e));
        };

        checkForUpdate();

        const intervalId = setInterval(() => {
            checkForUpdate();
            // 10 minutes
        }, 10 * 60 * 1000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setMessageBoxPropFunc = (prop: string) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBoxProp("");
        setTimeout(() => {
            setMessageBoxProp(prop);
        }, 10);
    };

    return (
        <>
            {isLoading ? (
                <Loading/>
            ) : (
                <div className="pageRoot">
                    <BrowserRouter>
                        <azureTokenContext.Provider value={azureToken}>
                            <userInfoContext.Provider value={[userInfo, setUserInfo]}>
                                <userIdContext.Provider value={userId}>
                                    <userRolesContext.Provider value={userRoles}>
                                        <messageBoxContext.Provider value={setMessageBoxProp}>
                                            <MessageBox type={messageBoxProp}/>
                                            <TopBar/>
                                            <MainPart/>
                                        </messageBoxContext.Provider>
                                    </userRolesContext.Provider>
                                </userIdContext.Provider>
                            </userInfoContext.Provider>
                        </azureTokenContext.Provider>
                    </BrowserRouter>
                </div>
            )}
        </>
    );
}

export const azureTokenContext = React.createContext<any>({});
export const userIdContext = React.createContext<string>("");
export const userInfoContext = React.createContext<any>({});
export const userRolesContext = React.createContext<string[]>([]);
export const messageBoxContext = React.createContext<any>("");
